/**
 * versionCheck — detect when a new deploy has shipped while the user has
 * the lounge open, and let them refresh into it with one tap.
 *
 * How it works
 * ------------
 * 1. On startup, fetch /version.json (written by deploy.sh as
 *    `{"version": "<TIMESTAMP>"}`) and remember the value as
 *    `bootVersion`.
 * 2. Every 60 seconds, refetch with cache-busting and compare against
 *    `bootVersion`.
 * 3. When they differ, surface a banner ("New Lounge update available —
 *    tap to refresh") with a dismiss control.
 * 4. On tap, hard-reload so the browser fetches a fresh index.html →
 *    fresh hashed bundle.
 *
 * Failure modes are silent — version.json missing, network errors, or
 * malformed JSON all just leave the user on their current version. The
 * banner only appears when we KNOW a new version is live.
 */

// Use a .txt extension instead of .json so Cloudflare doesn't apply
// its bot-challenge rule to it. The file is plain JSON content; we
// parse it manually.
const VERSION_URL = '/version.txt';
const POLL_INTERVAL_MS = 60 * 1000;
const DISMISSED_STORAGE_KEY = 'mw_version_dismissed';

type VersionPayload = { version?: string };

async function fetchVersion(): Promise<string | null> {
  try {
    // No query-string cache-buster — that triggers Cloudflare's bot
    // challenge on .json. We rely on the no-cache headers on
    // /version.txt (set in .htaccess via FilesMatch) for freshness.
    const res = await fetch(VERSION_URL, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!res.ok) return null;
    const text = await res.text();
    const data = JSON.parse(text) as VersionPayload;
    return typeof data.version === 'string' ? data.version : null;
  } catch {
    return null;
  }
}

type Listener = (newVersion: string) => void;

let bootVersion: string | null = null;
let pollTimer: number | null = null;
let listeners: Listener[] = [];

/** Subscribe to "a new version is available" events. */
export function onUpdateAvailable(listener: Listener): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

function notify(newVersion: string) {
  // If the user already dismissed THIS specific version, don't pester.
  try {
    if (localStorage.getItem(DISMISSED_STORAGE_KEY) === newVersion) return;
  } catch {
    /* private mode — surface the notice anyway */
  }
  listeners.forEach((l) => {
    try {
      l(newVersion);
    } catch {
      /* listener crashed — ignore so polling keeps running */
    }
  });
}

/** Remember that the user dismissed the banner for a specific version. */
export function dismissVersion(version: string) {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, version);
  } catch {
    /* ignore */
  }
}

/** Start the polling loop. Idempotent: safe to call multiple times. */
export function startVersionCheck() {
  if (pollTimer !== null) return;

  // Capture boot version. If the file doesn't exist (older deploy
  // without version.json) we leave bootVersion null and never compare.
  fetchVersion().then((v) => {
    bootVersion = v;
  });

  pollTimer = window.setInterval(async () => {
    if (document.hidden) return; // don't poll while tab is backgrounded
    const latest = await fetchVersion();
    if (!latest) return;
    if (bootVersion === null) {
      // We never got a boot version (file 404'd at startup). Adopt the
      // first successful fetch as boot.
      bootVersion = latest;
      return;
    }
    if (latest !== bootVersion) {
      notify(latest);
    }
  }, POLL_INTERVAL_MS);
}

/** Stop polling (used in tests or hot reload). */
export function stopVersionCheck() {
  if (pollTimer !== null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }
}
